import { useState } from 'react'
import type { CmsItem } from '../cmsTypes'
import { useI18n } from '../../../lib/i18n'
import { uploadAsset } from '../../../lib/api'
import { useToast } from '../../Toast'
import { IconArrowLeft, IconPhoto as ImageIcon } from '@tabler/icons-react'

export function CmsItemSettings({
  title,
  item,
  token,
  projectId,
  onBack,
  onSave,
}: {
  title: string
  item: CmsItem
  token: string
  projectId: string
  onBack: () => void
  onSave: (seo: { seoTitle: string; seoDescription: string; seoImage: string }) => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [seoTitle, setSeoTitle] = useState(item.values.seoTitle ?? '')
  const [seoDescription, setSeoDescription] = useState(item.values.seoDescription ?? '')
  const [seoImage, setSeoImage] = useState(item.values.seoImage ?? '')
  const [subiendo, setSubiendo] = useState(false)

  /**
   * La imagen se sube y se guarda su dirección; el archivo no entra en el
   * proyecto.
   *
   * Aquí se leía con `FileReader` y se guardaba el «data:image/...;base64,...»
   * entero dentro de la ficha. Eso mete la imagen —un tercio más grande, por la
   * codificación— en el JSON del proyecto: viaja en cada guardado, en cada carga
   * del editor y en cada publicación, multiplicado por el número de fichas. Con
   * cien posts son megabytes que no tenían por qué existir, y ninguna de esas
   * copias se puede cachear ni servir por separado.
   *
   * El resto del panel ya lo hacía bien: la portada de una página, la de una
   * oferta y la biblioteca de imágenes suben a R2 y guardan la URL. Esto era el
   * único sitio que se había quedado atrás.
   */
  async function subirPortada(file: File | undefined) {
    if (!file) return
    if (!file.type.startsWith('image/')) { showToast(t('pageSettings.notImage'), 'error'); return }
    if (file.size > 10 * 1024 * 1024) { showToast(t('pageSettings.tooBig'), 'error'); return }
    setSubiendo(true)
    try {
      const { url } = await uploadAsset(token, file, projectId)
      setSeoImage(url)
    } catch {
      showToast(t('pageSettings.uploadFailed'), 'error')
    } finally {
      setSubiendo(false)
    }
  }

  /* Con las piezas del panel (`dash-field`) y no con estilos escritos aquí:
     así se adapta al teléfono y al modo oscuro como todo lo demás. Antes los
     colores iban fijos —bordes grises de modo claro— y en oscuro no se veían. */
  return (
    <>
      <div className="dash-cms-seo-head">
        <div className="dash-cms-seo-heading">
          {/* La flecha sola no dice a dónde vuelve para quien no la ve. */}
          <button type="button" className="dash-icon-btn" onClick={onBack} aria-label={t('cms.seo.back')}><IconArrowLeft size={16} /></button>
          <span className="dash-card-title">{t('cms.seo.heading', { title })}</span>
        </div>
        <button type="button" className="dash-primary-btn" onClick={() => onSave({ seoTitle, seoDescription, seoImage })}>{t('cms.seo.save')}</button>
      </div>

      <div className="dash-cms-seo-body">
        <h4 className="dash-cms-seo-section">{t('cms.seo.section')}</h4>
        <label className="dash-field">
          <span>{t('cms.seo.titleLabel')}</span>
          <input value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} placeholder={t('cms.seo.titlePlaceholder')} />
          <small className={seoTitle.length > 60 ? 'is-warn' : undefined}>{t('cms.seo.titleCount', { count: seoTitle.length })}</small>
        </label>
        <label className="dash-field">
          <span>{t('cms.seo.descLabel')}</span>
          <textarea value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} placeholder={t('cms.seo.descPlaceholder')} />
          <small className={seoDescription.length > 160 ? 'is-warn' : undefined}>{t('cms.seo.descCount', { count: seoDescription.length })}</small>
        </label>
        <div className="dash-field">
          <span>{t('cms.seo.imageLabel')}</span>
          {seoImage ? (
            <div className="dash-cms-seo-image">
              <img src={seoImage} alt={t('cms.seo.imageAlt')} />
              <button type="button" onClick={() => setSeoImage('')} aria-label={t('cms.seo.removeImage')}>×</button>
            </div>
          ) : (
            <label className="dash-cms-seo-upload">
              <ImageIcon size={18} />
              {subiendo ? t('cms.seo.uploading') : t('cms.seo.upload')}
              <input type="file" accept="image/*" hidden disabled={subiendo} onChange={(e) => { void subirPortada(e.target.files?.[0]); e.target.value = '' }} />
            </label>
          )}
        </div>
      </div>
    </>
  )
}
